import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { ApiError, api } from '../lib/api';
import {
  dateTime,
  money,
  nextOrderStatuses,
  orderLabels,
  shortDate,
  stageLabels,
  todayInput,
} from '../lib/format';
import { fieldErrors } from '../lib/form-errors';
import { ORDER_TAGS_LIMIT, parseTagInput, tagInputError } from '../lib/order-tags';
import { haptic } from '../lib/telegram';
import type {
  ListResponse,
  Order,
  OrderComment,
  OrderAttachment,
  OrderStage,
  OrderStatus,
  Payment,
  PaymentType,
} from '../types';
import { AttachmentPicker } from '../components/attachment-picker';
import { ClosedOrderContact } from '../components/closed-order-contact';
import { CommentThread } from '../components/comment-thread';
import { ImageField } from '../components/image-field';
import { OrderFiles } from '../components/order-files';
import { ArtistReferences } from '../components/order-references';
import { buildOrderTimeline } from '../lib/order-timeline';
import { TemplatePicker } from '../components/template-picker';
import { PaymentClaims } from '../components/payment-claims';
import {
  Button,
  Card,
  ErrorNotice,
  Field,
  Input,
  LoadingScreen,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
} from '../components/ui';

/**
 * Переписка с заказчиком. Художник должен уметь написать первым — обсудить
 * заказ до начала работы, а не только отвечать на сообщения. Приватная заметка
 * остаётся внутренней: заказчику она не видна ни здесь, ни в его кабинете.
 */
function OrderThread({ order }: { order: Order }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  // По закрытому заказу остаётся только приватная заметка: ответить некому.
  const closed = order.status === 'COMPLETED' || order.status === 'CANCELED';
  const [visibility, setVisibility] = useState<'PUBLIC' | 'PRIVATE'>(closed ? 'PRIVATE' : 'PUBLIC');
  const comments = useQuery({
    queryKey: ['order', order.id, 'comments'],
    queryFn: () => api<ListResponse<OrderComment>>(`/orders/${order.id}/comments`),
  });
  const send = useMutation({
    mutationFn: () =>
      api<OrderComment>(`/orders/${order.id}/comments`, {
        method: 'POST',
        body: { body: body.trim(), visibility },
      }),
    onSuccess: () => {
      haptic('success');
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['order', order.id, 'comments'] });
    },
  });

  return (
    <>
      <div className="section-heading">
        <h2>Переписка</h2>
        <span className="meta">{order.client.displayName}</span>
      </div>
      <Card className="stack stack--compact">
        {comments.isPending ? (
          <p className="muted">Загружаем сообщения…</p>
        ) : comments.error ? (
          <ErrorNotice error={comments.error} />
        ) : (
          <CommentThread
            comments={comments.data.items}
            emptyText="Пока тихо. Напишите заказчику первым — отсюда или командой /chat в боте, ему придёт уведомление."
            counterpartName={order.client.displayName}
          />
        )}

        {closed && <ClosedOrderContact email={order.customerEmail} who="Заказчик" />}

        <form
          // Ошибки показывают сами поля: браузерная подсказка их дублировала.
          noValidate
          className="stack stack--compact"
          onSubmit={(event) => {
            event.preventDefault();
            send.mutate();
          }}
        >
          <Field
            label={visibility === 'PRIVATE' ? 'Заметка для себя' : 'Сообщение заказчику'}
            hint={
              visibility === 'PRIVATE'
                ? 'Видно только вам'
                : 'Придёт в Telegram и появится в кабинете заказчика'
            }
            error={fieldErrors(send.error).body}
          >
            <textarea
              className="input input--textarea"
              rows={3}
              maxLength={2000}
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </Field>
          {/* Вставка дописывает в конец, а не затирает: художник обычно
              начинает со своего и добавляет заготовку, а не наоборот. */}
          <TemplatePicker
            orderId={order.id}
            onPick={(text) => setBody((current) => (current ? `${current}\n${text}` : text))}
          />
          <div className="thread-controls">
            <Select
              value={visibility}
              onChange={(event) => setVisibility(event.target.value as 'PUBLIC' | 'PRIVATE')}
            >
              {!closed && <option value="PUBLIC">Заказчику</option>}
              <option value="PRIVATE">Приватная заметка</option>
            </Select>
            <Button type="submit" disabled={!body.trim() || send.isPending}>
              {send.isPending ? 'Отправляем…' : 'Отправить'}
            </Button>
          </div>
          {send.error && <ErrorNotice error={send.error} />}
        </form>
      </Card>
    </>
  );
}

const PAYMENT_FIELD_LABELS: Record<string, string> = {
  type: 'Тип',
  amount: 'Сумма',
  method: 'Способ',
  paidAt: 'Дата оплаты',
  comment: 'Комментарий',
};

const ORDER_FIELD_LABELS: Record<string, string> = {
  title: 'Название',
  description: 'Описание',
  totalPrice: 'Стоимость',
  depositRequired: 'Предоплата',
  deadline: 'Дедлайн',
  revisionLimit: 'Лимит правок',
  revisionUsed: 'Использовано правок',
  tags: 'Теги',
  installmentParts: 'Частей в рассрочке',
  estimatedDays: 'Работы на, дней',
  plannedStartAt: 'Начать не раньше',
  artistPrivateNote: 'Приватная заметка',
  clientPublicNote: 'Комментарий клиенту',
};

const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  DEPOSIT: 'Предоплата',
  PARTIAL: 'Частичная оплата',
  FINAL: 'Финальная оплата',
  REFUND: 'Возврат',
  OTHER: 'Другое',
};

export function OrderPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [stageTarget, setStageTarget] = useState<OrderStage | null>(null);
  const query = useQuery({
    queryKey: ['order', id],
    queryFn: () => api<Order>(`/orders/${id}`),
  });
  const statusMutation = useMutation({
    mutationFn: ({ order, status }: { order: Order; status: OrderStatus }) =>
      api<Order>(`/orders/${order.id}/status`, {
        method: 'POST',
        body: { version: order.version, status, notifyClient: true },
      }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['order', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
    },
  });

  if (query.isPending) return <LoadingScreen label="Открываем заказ" />;
  if (query.error) return <ErrorNotice error={query.error} />;
  const order = query.data;
  const pausedFrom =
    order.status === 'PAUSED'
      ? [...order.history].reverse().find((item) => item.newStatus === 'PAUSED')?.previousStatus
      : null;
  const timeline = buildOrderTimeline(order.stageHistory, order.history);
  const available: OrderStatus[] =
    order.status === 'PAUSED' && pausedFrom
      ? [pausedFrom, 'CANCELED']
      : nextOrderStatuses[order.status];

  return (
    <>
      {/* Стрелка «назад» живёт в шапке приложения, здесь она была бы второй. */}
      <PageHeader
        eyebrow={order.publicNumber}
        title={order.title}
        action={
          <Button variant="ghost" onClick={() => setEditOpen(true)}>
            Изменить
          </Button>
        }
      />
      <div className="order-hero">
        <StatusBadge
          tone={
            order.status === 'IN_PROGRESS'
              ? 'accent'
              : order.status === 'COMPLETED'
                ? 'success'
                : 'neutral'
          }
        >
          {orderLabels[order.status]}
        </StatusBadge>
        {order.archivedAt && <StatusBadge tone="neutral">В архиве</StatusBadge>}
        {!order.showPriceToCustomer && <StatusBadge tone="warning">Цена скрыта</StatusBadge>}
        <p>
          {/* Из заказа всегда нужен переход к истории клиента, а не только имя. */}
          <Link href={`/clients/${order.client.id}`} className="link-inline">
            {order.client.displayName}
          </Link>
        </p>
        {order.tags.length > 0 && (
          <div className="tag-list" aria-label="Теги заказа">
            {order.tags.map((tag) => (
              <span key={tag} className="tag-pill">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="detail-grid detail-grid--cards">
        <Card>
          <span className="meta">Дедлайн</span>
          <strong className={order.deadlineState === 'OVERDUE' ? 'text-danger' : ''}>
            {shortDate(order.deadline)}
          </strong>
        </Card>
        <Card>
          <span className="meta">Очередь</span>
          <strong>{order.queuePosition ? `№ ${order.queuePosition}` : 'Не в очереди'}</strong>
        </Card>
      </div>

      {/*
        Этап — отдельный автомат от статуса, поэтому у него свой блок. Список
        доступных переходов приходит с сервера: повторять таблицу переходов на
        клиенте значит рано или поздно с ней разойтись.
      */}
      <div className="section-heading">
        <h2>Этап работы</h2>
        <span className="meta">{dateTime(order.stageUpdatedAt)}</span>
      </div>
      <Card className="stage-card">
        <div className="card-row">
          <strong>{stageLabels[order.currentStage]}</strong>
          <span className="meta">{order.progress}%</span>
        </div>
        <div className="payment-progress">
          <span style={{ width: `${order.progress}%` }} />
        </div>
        {order.availableStages.length > 0 ? (
          <div className="status-actions">
            {order.availableStages.map((stage) => (
              <Button
                key={stage}
                variant={stage === 'CANCELED' ? 'ghost' : 'secondary'}
                onClick={() => setStageTarget(stage)}
              >
                {stageLabels[stage]}
              </Button>
            ))}
          </div>
        ) : (
          <p className="muted">Работа завершена — этапы больше не меняются.</p>
        )}
      </Card>

      <Card className="payment-card">
        <div className="card-row">
          <div>
            <p className="eyebrow">Оплата</p>
            <h2>{money(order.payment.paidAmount, order.currency)}</h2>
          </div>
          <Button variant="secondary" onClick={() => setPaymentOpen(true)}>
            + Платёж
          </Button>
        </div>
        <div className="payment-progress payment-progress--large">
          <span
            style={{
              width: `${Math.min(100, (Number(order.payment.paidAmount) / Number(order.totalPrice || 1)) * 100)}%`,
            }}
          />
        </div>
        <div className="card-row">
          <span>Итого {money(order.totalPrice, order.currency)}</span>
          <strong>Остаток {money(order.payment.balanceDue, order.currency)}</strong>
        </div>
        {/* Рассрочка: бот напоминает об остатке, пока она не закрыта. */}
        {order.installment && (
          <p className="meta">
            Рассрочка: {order.installment.paidParts} из {order.installment.parts} частей по{' '}
            {money(order.installment.partAmount, order.currency)}
          </p>
        )}
      </Card>

      {/* Референсы держим выше файлов: художник возвращается к ним чаще всего. */}
      <ArtistReferences
        references={order.attachments.filter((file) => file.kind === 'REFERENCE')}
      />

      <OrderFiles orderId={order.id} />

      <ShowcaseImageCard order={order} />

      {/* Сообщения заказчика идут выше журнала: сначала решение, потом запись. */}
      <PaymentClaims order={order} />

      <PaymentJournal order={order} />

      {available.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Изменить статус</h2>
          </div>
          <div className="status-actions">
            {available.map((status) => (
              <Button
                key={status}
                variant={status === 'CANCELED' ? 'ghost' : 'secondary'}
                disabled={statusMutation.isPending}
                onClick={() => statusMutation.mutate({ order, status })}
              >
                {orderLabels[status]}
              </Button>
            ))}
          </div>
          {statusMutation.error && (
            <OrderErrorNotice
              error={statusMutation.error}
              onReload={() => {
                statusMutation.reset();
                void query.refetch();
              }}
            />
          )}
        </>
      )}

      <div className="section-heading">
        <h2>О заказе</h2>
      </div>
      <Card className="stack stack--compact">
        <div className="prose-block">
          <span>Описание</span>
          <p>{order.description}</p>
        </div>
        <div className="detail-grid">
          <div>
            <span>Услуга</span>
            <strong>{order.service?.title ?? 'Без услуги'}</strong>
          </div>
          <div>
            <span>Правки</span>
            <strong>
              {order.revisionUsed} из {order.revisionLimit}
            </strong>
          </div>
        </div>
        {order.clientPublicNote && (
          <div className="prose-block">
            <span>Комментарий клиенту</span>
            <p>{order.clientPublicNote}</p>
          </div>
        )}
        {/* Приватная заметка не уходит заказчику ни в кабинет, ни в уведомление. */}
        {order.artistPrivateNote && (
          <div className="prose-block prose-block--private">
            <span>Приватная заметка</span>
            <p>{order.artistPrivateNote}</p>
          </div>
        )}
      </Card>

      <OrderThread order={order} />

      {/*
        Одна лента вместо двух. Этап и статус — связанные автоматы: перевод
        этапа почти всегда двигает и статус, и раздельные истории показывали
        одно событие дважды, разными словами.
      */}
      <div className="section-heading">
        <h2>История</h2>
      </div>
      <Card>
        {timeline.length === 0 ? (
          <p className="muted">Работа пока на первом этапе — здесь появится весь её путь.</p>
        ) : (
          <div className="timeline">
            {timeline.map((entry) => (
              <div className="timeline__item" key={entry.id}>
                <span className="timeline__dot" />
                <div>
                  <strong>
                    {entry.stage
                      ? `${entry.stage.from ? `${stageLabels[entry.stage.from]} → ` : ''}${stageLabels[entry.stage.to]}`
                      : orderLabels[entry.status!]}
                  </strong>
                  {/* Статус называем, только когда он не повторяет этап. */}
                  {entry.stage && entry.status && (
                    <p className="meta">Статус: {orderLabels[entry.status].toLowerCase()}</p>
                  )}
                  <p>{dateTime(entry.at)}</p>
                  {entry.publicComment && <small>{entry.publicComment}</small>}
                  {entry.privateComment && (
                    <small className="text-private">Приватно: {entry.privateComment}</small>
                  )}
                  {entry.attachments > 0 && <small>Вложений: {entry.attachments}</small>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {paymentOpen && <PaymentModal order={order} onClose={() => setPaymentOpen(false)} />}
      {editOpen && (
        <EditOrderModal
          order={order}
          onClose={() => setEditOpen(false)}
          onArchived={() => navigate('/orders')}
        />
      )}
      {stageTarget && (
        <StageModal order={order} stage={stageTarget} onClose={() => setStageTarget(null)} />
      )}
    </>
  );
}

/**
 * Одна картинка готовой работы. До неё витрина показывала обложку услуги —
 * то есть чужой пример вместо самого результата. Показ в профиле по-прежнему
 * включает заказчик: художник только прикладывает файл.
 */
function ShowcaseImageCard({ order }: { order: Order }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (showcaseImageId: string | null) =>
      api<Order>(`/orders/${order.id}`, {
        method: 'PATCH',
        body: { version: order.version, showcaseImageId },
      }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['order', order.id], updated);
    },
  });

  if (order.status !== 'COMPLETED' && order.currentStage !== 'READY') {
    return null;
  }

  return (
    <>
      <div className="section-heading">
        <h2>Готовая работа</h2>
      </div>
      <Card className="stack stack--compact">
        <ImageField
          label="Картинка для витрины"
          hint="Одна картинка для витрины профиля"
          kind="cover"
          url={order.showcaseUrl}
          disabled={mutation.isPending}
          onChange={(imageId) => mutation.mutate(imageId)}
        />
        <p className="muted settings-note">
          {order.showcase === 'HIDDEN'
            ? 'Показ в профиле разрешает заказчик — пока не разрешил, работы не видно.'
            : 'Заказчик разрешил показ — работа в профиле.'}
        </p>
        {mutation.error && <ErrorNotice error={mutation.error} />}
      </Card>
    </>
  );
}

/**
 * Конфликт версии — не ошибка ввода: заказ изменили в другом окне или в боте.
 * Повтор того же действия снова упрётся в версию, поэтому вместо общего текста
 * предлагаем перечитать карточку.
 */
function OrderErrorNotice({
  error,
  onReload,
  labels,
}: {
  error: unknown;
  onReload: () => void;
  labels?: Record<string, string>;
}) {
  if (!(error instanceof ApiError) || error.code !== 'VERSION_CONFLICT') {
    return <ErrorNotice error={error} labels={labels} />;
  }

  return (
    <div className="notice notice--error stack stack--compact">
      <span>{error.message}</span>
      <Button variant="secondary" onClick={onReload}>
        Загрузить актуальные данные
      </Button>
    </div>
  );
}

/**
 * Журнал платежей. Финансовая запись не удаляется физически: ошибочную
 * операцию аннулируют, и она остаётся в списке зачёркнутой — иначе баланс
 * невозможно свести с историей.
 */
function PaymentJournal({ order }: { order: Order }) {
  const queryClient = useQueryClient();
  const [voiding, setVoiding] = useState<Payment | null>(null);
  const mutation = useMutation({
    mutationFn: (payment: Payment) =>
      api<Order>(`/orders/${order.id}/payments/${payment.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      haptic('success');
      setVoiding(null);
      void queryClient.invalidateQueries({ queryKey: ['order', order.id] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  return (
    <>
      <div className="section-heading">
        <h2>Журнал платежей</h2>
        <span className="meta">{order.payments.length}</span>
      </div>
      <Card className="stack stack--compact">
        {order.payments.length === 0 ? (
          <p className="muted">
            Платежей пока нет. Записывайте их кнопкой «+ Платёж» — остаток посчитается сам.
          </p>
        ) : (
          <div className="payment-log">
            {order.payments.map((payment) => (
              <div
                key={payment.id}
                className={`payment-log__row ${payment.voidedAt ? 'is-voided' : ''}`}
              >
                <div>
                  <strong>{PAYMENT_TYPE_LABELS[payment.type]}</strong>
                  <span className="meta">
                    {shortDate(payment.paidAt)} · {payment.method}
                  </span>
                  {payment.comment && <small>{payment.comment}</small>}
                  {payment.voidedAt && (
                    <small className="text-danger">Аннулирован {shortDate(payment.voidedAt)}</small>
                  )}
                </div>
                <div className="payment-log__amount">
                  <strong className={payment.type === 'REFUND' ? 'text-danger' : ''}>
                    {payment.type === 'REFUND' ? '−' : ''}
                    {money(payment.amount, payment.currency)}
                  </strong>
                  {!payment.voidedAt && (
                    <button type="button" onClick={() => setVoiding(payment)}>
                      Аннулировать
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {mutation.error && <ErrorNotice error={mutation.error} />}
      </Card>

      {voiding && (
        <Modal title="Аннулировать платёж" onClose={() => setVoiding(null)}>
          <div className="stack">
            <p className="muted">
              {PAYMENT_TYPE_LABELS[voiding.type]} на {money(voiding.amount, voiding.currency)} от{' '}
              {shortDate(voiding.paidAt)} перестанет считаться в балансе, но из журнала не пропадёт
              — так итог всегда сходится с историей.
            </p>
            {mutation.error && <ErrorNotice error={mutation.error} />}
            <Button
              variant="danger"
              wide
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(voiding)}
            >
              {mutation.isPending ? 'Аннулируем…' : 'Аннулировать'}
            </Button>
            <Button variant="ghost" wide onClick={() => setVoiding(null)}>
              Отмена
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * Смена рабочего этапа. Статус заказа пересчитывает сервер по своим правилам —
 * здесь художник только выбирает этап и решает, что увидит заказчик.
 */
function StageModal({
  order,
  stage,
  onClose,
}: {
  order: Order;
  stage: OrderStage;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [publicComment, setPublicComment] = useState('');
  const [privateComment, setPrivateComment] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [attachments, setAttachments] = useState<OrderAttachment[]>([]);
  const mutation = useMutation({
    mutationFn: () =>
      api<Order>(`/artist/orders/${order.id}/stage`, {
        method: 'POST',
        body: {
          version: order.version,
          stage,
          publicComment: publicComment.trim() || null,
          privateComment: privateComment.trim() || null,
          notifyCustomer,
          attachmentIds: attachments.map((attachment) => attachment.id),
        },
      }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['order', order.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
      onClose();
    },
  });

  return (
    <Modal title={`Этап: ${stageLabels[stage]}`} onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <p className="muted">
          Сейчас: {stageLabels[order.currentStage]}. Статус заказа обновится сам — следить за ним не
          нужно.
        </p>
        <Field label="Комментарий заказчику" hint="Придёт заказчику в Telegram">
          <textarea
            className="input input--textarea"
            rows={3}
            maxLength={2000}
            value={publicComment}
            onChange={(event) => setPublicComment(event.target.value)}
          />
        </Field>
        <TemplatePicker
          kind="STAGE"
          orderId={order.id}
          onPick={(text) => setPublicComment((current) => (current ? `${current}\n${text}` : text))}
        />
        <Field label="Приватная заметка" hint="Заметка для себя: заказчик её не увидит">
          <textarea
            className="input input--textarea"
            rows={2}
            maxLength={2000}
            value={privateComment}
            onChange={(event) => setPrivateComment(event.target.value)}
          />
        </Field>
        {/* Этап без картинки — это слова: скетч и промежуточный результат
            показывают файлом, поэтому загрузка живёт прямо в переходе. */}
        <AttachmentPicker
          orderId={order.id}
          visibility="PUBLIC"
          attachments={attachments}
          onChange={setAttachments}
        />
        <label className="toggle-row">
          <span>Уведомить заказчика</span>
          <button
            type="button"
            role="switch"
            aria-checked={notifyCustomer}
            aria-label="Уведомить заказчика"
            className={`switch ${notifyCustomer ? 'is-on' : ''}`}
            onClick={() => setNotifyCustomer(!notifyCustomer)}
          >
            <span />
          </button>
        </label>
        {mutation.error && (
          <OrderErrorNotice
            error={mutation.error}
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: ['order', order.id] });
              onClose();
            }}
          />
        )}
        <Button type="submit" wide disabled={mutation.isPending}>
          {mutation.isPending ? 'Сохраняем…' : `Перевести в «${stageLabels[stage]}»`}
        </Button>
      </form>
    </Modal>
  );
}

function PaymentModal({ order, onClose }: { order: Order; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<PaymentType>('DEPOSIT');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Перевод');
  const [comment, setComment] = useState('');
  // Деньги нередко заносят задним числом, поэтому дата редактируется.
  const [paidAt, setPaidAt] = useState(todayInput());
  const mutation = useMutation({
    mutationFn: () =>
      api(`/orders/${order.id}/payments`, {
        method: 'POST',
        body: { type, amount, method, paidAt, comment: comment || null },
      }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['order', order.id] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      onClose();
    },
  });
  const errors = fieldErrors(mutation.error);

  return (
    <Modal title="Добавить платёж" onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <Field label="Тип" error={errors.type}>
          <Select value={type} onChange={(event) => setType(event.target.value as PaymentType)}>
            <option value="DEPOSIT">Предоплата</option>
            <option value="PARTIAL">Частичная оплата</option>
            <option value="FINAL">Финальная оплата</option>
            <option value="REFUND">Возврат</option>
            <option value="OTHER">Другое</option>
          </Select>
        </Field>
        <Field label={`Сумма, ${order.currency}`} error={errors.amount}>
          <Input
            required
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder={order.payment.balanceDue}
          />
        </Field>
        <div className="form-grid">
          <Field label="Способ" error={errors.method}>
            <Select value={method} onChange={(event) => setMethod(event.target.value)}>
              <option>Перевод</option>
              <option>Наличные</option>
              <option>Банковская карта</option>
              <option>Электронный кошелёк</option>
              <option>Другое</option>
            </Select>
          </Field>
          <Field label="Дата оплаты" error={errors.paidAt}>
            <Input
              type="date"
              max={todayInput()}
              value={paidAt}
              onChange={(event) => setPaidAt(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Комментарий" error={errors.comment}>
          <Input value={comment} onChange={(event) => setComment(event.target.value)} />
        </Field>
        {mutation.error && <ErrorNotice error={mutation.error} labels={PAYMENT_FIELD_LABELS} />}
        <Button type="submit" wide disabled={!amount || mutation.isPending}>
          {mutation.isPending ? 'Сохраняем…' : 'Сохранить платёж'}
        </Button>
      </form>
    </Modal>
  );
}

/**
 * Редактирование заказа. Отправляем только изменённые поля: `PATCH` требует
 * хотя бы одно поле и отклоняет лишние ключи как строгий объект. Версия уходит
 * вместе с телом — сервер вернёт `VERSION_CONFLICT`, если карточку успели
 * изменить в другом окне.
 */
function EditOrderModal({
  order,
  onClose,
  onArchived,
}: {
  order: Order;
  onClose: () => void;
  onArchived: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(order.title);
  const [description, setDescription] = useState(order.description);
  const [totalPrice, setTotalPrice] = useState(order.totalPrice);
  const [depositRequired, setDepositRequired] = useState(order.depositRequired);
  const [deadline, setDeadline] = useState(order.deadline ?? '');
  const [tags, setTags] = useState(order.tags.join(', '));
  const [revisionLimit, setRevisionLimit] = useState(String(order.revisionLimit));
  const [revisionUsed, setRevisionUsed] = useState(String(order.revisionUsed));
  const [clientPublicNote, setClientPublicNote] = useState(order.clientPublicNote ?? '');
  const [artistPrivateNote, setArtistPrivateNote] = useState(order.artistPrivateNote ?? '');
  const [showPrice, setShowPrice] = useState(order.showPriceToCustomer);
  const [installmentParts, setInstallmentParts] = useState(
    order.installment ? String(order.installment.parts) : '',
  );
  const [estimatedDays, setEstimatedDays] = useState(
    order.estimatedDays ? String(order.estimatedDays) : '',
  );
  const [plannedStartAt, setPlannedStartAt] = useState(order.plannedStartAt ?? '');
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Order>(`/orders/${order.id}`, {
        method: 'PATCH',
        body: { version: order.version, ...body },
      }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['order', order.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
    },
  });
  const errors = fieldErrors(save.error);
  const parsedTags = parseTagInput(tags);
  const parts = installmentParts.trim() === '' ? null : Number(installmentParts);
  const tagsError = tagInputError(tags) ?? errors.tags;
  const partsError =
    parts !== null && (!Number.isInteger(parts) || parts < 2 || parts > 12)
      ? 'От 2 до 12 частей'
      : errors.installmentParts;
  const usedError =
    Number(revisionUsed) > Number(revisionLimit)
      ? 'Использованных правок не может быть больше лимита'
      : errors.revisionUsed;
  const depositError =
    Number(depositRequired) > Number(totalPrice)
      ? 'Предоплата не может быть больше стоимости'
      : errors.depositRequired;
  const body = changedFields();
  const invalid =
    !title.trim() ||
    !description.trim() ||
    Boolean(tagsError) ||
    Boolean(partsError) ||
    Boolean(usedError) ||
    Boolean(depositError);

  function changedFields(): Record<string, unknown> {
    const changed: Record<string, unknown> = {};
    if (title.trim() !== order.title) changed.title = title.trim();
    if (description.trim() !== order.description) changed.description = description.trim();
    if (totalPrice !== order.totalPrice) changed.totalPrice = totalPrice;
    if (depositRequired !== order.depositRequired) changed.depositRequired = depositRequired;
    if ((deadline || null) !== order.deadline) changed.deadline = deadline || null;
    if (parsedTags.join(',') !== order.tags.join(',')) changed.tags = parsedTags;
    if (Number(revisionLimit) !== order.revisionLimit) {
      changed.revisionLimit = Number(revisionLimit);
    }
    if (Number(revisionUsed) !== order.revisionUsed) changed.revisionUsed = Number(revisionUsed);
    if ((clientPublicNote.trim() || null) !== order.clientPublicNote) {
      changed.clientPublicNote = clientPublicNote.trim() || null;
    }
    if ((artistPrivateNote.trim() || null) !== order.artistPrivateNote) {
      changed.artistPrivateNote = artistPrivateNote.trim() || null;
    }
    if (showPrice !== order.showPriceToCustomer) changed.showPriceToCustomer = showPrice;
    if (parts !== (order.installment?.parts ?? null)) changed.installmentParts = parts;
    const days = estimatedDays.trim() === '' ? null : Number(estimatedDays);
    if (days !== order.estimatedDays) changed.estimatedDays = days;
    if ((plannedStartAt || null) !== order.plannedStartAt) {
      changed.plannedStartAt = plannedStartAt || null;
    }
    return changed;
  }

  return (
    <Modal title="Редактировать заказ" onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(body, { onSuccess: onClose });
        }}
      >
        <Field label="Название" error={errors.title}>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>
        <Field label="Описание" error={errors.description}>
          <textarea
            className="input input--textarea"
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <div className="form-grid">
          <Field label={`Стоимость, ${order.currency}`} error={errors.totalPrice}>
            <Input
              inputMode="decimal"
              value={totalPrice}
              onChange={(event) => setTotalPrice(event.target.value)}
            />
          </Field>
          <Field label={`Предоплата, ${order.currency}`} error={depositError}>
            <Input
              inputMode="decimal"
              value={depositRequired}
              onChange={(event) => setDepositRequired(event.target.value)}
            />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="Дедлайн" hint="Пусто — работаете без срока" error={errors.deadline}>
            <Input
              type="date"
              value={deadline}
              onChange={(event) => setDeadline(event.target.value)}
            />
          </Field>
          <Field label="Частей в рассрочке" hint="Пусто — оплата целиком" error={partsError}>
            <Input
              type="number"
              min={2}
              max={12}
              value={installmentParts}
              onChange={(event) => setInstallmentParts(event.target.value)}
            />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="Лимит правок" error={errors.revisionLimit}>
            <Input
              type="number"
              min={0}
              max={50}
              value={revisionLimit}
              onChange={(event) => setRevisionLimit(event.target.value)}
            />
          </Field>
          <Field label="Использовано правок" error={usedError}>
            <Input
              type="number"
              min={0}
              max={50}
              value={revisionUsed}
              onChange={(event) => setRevisionUsed(event.target.value)}
            />
          </Field>
        </div>
        {/* Оценка кормит прогноз загрузки: без неё заказ в расписание не попадает. */}
        <div className="form-grid">
          <Field
            label="Работы на, дней"
            hint="Рабочих дней. Пусто — заказ не попадёт в прогноз"
            error={errors.estimatedDays}
          >
            <Input
              type="number"
              min={1}
              max={365}
              value={estimatedDays}
              onChange={(event) => setEstimatedDays(event.target.value)}
            />
          </Field>
          <Field
            label="Начать не раньше"
            hint="Пусто — как освободитесь"
            error={errors.plannedStartAt}
          >
            <Input
              type="date"
              value={plannedStartAt}
              onChange={(event) => setPlannedStartAt(event.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Теги"
          hint={`До ${ORDER_TAGS_LIMIT} тегов через запятую — по ним вас найдут те, кому нужно такое же`}
          error={tagsError}
        >
          <Input value={tags} onChange={(event) => setTags(event.target.value)} />
        </Field>
        <Field label="Комментарий клиенту" error={errors.clientPublicNote}>
          <textarea
            className="input input--textarea"
            rows={2}
            value={clientPublicNote}
            onChange={(event) => setClientPublicNote(event.target.value)}
          />
        </Field>
        <Field
          label="Приватная заметка"
          hint="Заметка для себя: заказчик её не увидит"
          error={errors.artistPrivateNote}
        >
          <textarea
            className="input input--textarea"
            rows={2}
            value={artistPrivateNote}
            onChange={(event) => setArtistPrivateNote(event.target.value)}
          />
        </Field>
        <label className="toggle-row">
          <span>Показывать цену заказчику</span>
          <button
            type="button"
            role="switch"
            aria-checked={showPrice}
            aria-label="Показывать цену заказчику"
            className={`switch ${showPrice ? 'is-on' : ''}`}
            onClick={() => setShowPrice(!showPrice)}
          >
            <span />
          </button>
        </label>
        {save.error && (
          <OrderErrorNotice
            error={save.error}
            labels={ORDER_FIELD_LABELS}
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: ['order', order.id] });
              onClose();
            }}
          />
        )}
        <Button
          type="submit"
          wide
          disabled={invalid || Object.keys(body).length === 0 || save.isPending}
        >
          {save.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
        {/*
          Архив убирает заказ из активных списков и из лимита тарифа, но
          сохраняет историю и платежи: физически ничего не удаляется.
        */}
        <Button
          variant="ghost"
          wide
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              { archived: order.archivedAt === null },
              { onSuccess: order.archivedAt ? onClose : onArchived },
            )
          }
        >
          {order.archivedAt ? 'Вернуть из архива' : 'В архив'}
        </Button>
      </form>
    </Modal>
  );
}
